import React, {useContext, useEffect, useMemo, useState} from "react";
import {getUserSignDocsAPI} from "../../../Services/api";
import {isEmpty, map, get, groupBy, maxBy} from "lodash";
import {SignDocCard} from "../../SignDoc/SignDocCard/SignDocCard";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {I18nReactContext} from "../../../contexts/I18nContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {useNavigate} from 'react-router-dom';
import './MyArchivedSignDocsContent.scss';

/**
 * MyArchivedSignDocsContent - Renders user's archived recurring SignDocs
 * Groups by signDoc.groupRootId and shows latest version per group
 * Clicking navigates to versions list page
 *
 * @param {Object} props - Component props
 * @param {Array} props.types - Array of SignDoc types to fetch
 * @param {Array} props.categories - Array of categories to fetch
 * @param {Function} props.filterSignDocsBySearch - Filter function from parent
 */
export const MyArchivedSignDocsContent = ({
    types,
    categories,
    filterSignDocsBySearch
}) => {
    const [archivedGroups, setArchivedGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const companyContext = useContext(CompanyReactContext);
    const {i18n} = useContext(I18nReactContext);
    const navigate = useNavigate();

    /**
     * Fetches archived UserSignDocs and groups them by groupRootId
     */
    const initArchivedSignDocs = async () => {
        setIsLoading(true);
        try {
            const archivedUserSignDocs = await getUserSignDocsAPI({
                types,
                categories,
                userSignDocStatus: 'ARCHIVED',
                hideLearningPathItems: true
            });

            // Ensure we have an array before grouping
            if (!Array.isArray(archivedUserSignDocs)) {
                setArchivedGroups([]);
                return;
            }

            // Group by signDoc.groupRootId and get latest version per group
            const grouped = groupBy(archivedUserSignDocs, item => get(item, 'signDoc.groupRootId'));
            const latestPerGroup = Object.entries(grouped).map(([groupRootId, items]) => {
                // Get the latest version (highest groupVersion)
                const latest = maxBy(items, item => get(item, 'signDoc.groupVersion', 0));
                return {
                    ...latest,
                    groupRootId,
                    totalVersions: items.length
                };
            });

            setArchivedGroups(latestPerGroup);
        } catch (error) {
            console.error('Error fetching archived SignDocs:', error);
            setArchivedGroups([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isEmpty(companyContext.featureFlags)) {
            return;
        }
        initArchivedSignDocs();
    }, [companyContext.featureFlags]);

    // Apply parent's filter function
    const filteredGroups = useMemo(() => {
        if (!filterSignDocsBySearch) {
            return archivedGroups;
        }
        return filterSignDocsBySearch({signDocs: archivedGroups});
    }, [archivedGroups, filterSignDocsBySearch]);

    // Check if empty state is due to filters
    const isEmptyDueToFilters = useMemo(() => {
        return archivedGroups.length > 0 && filteredGroups.length === 0;
    }, [archivedGroups.length, filteredGroups.length]);

    /**
     * Handle versions button click - navigate to versions page
     * Only used when there are multiple archived versions
     * @param {Object} params - Parameters
     * @param {string} params.groupRootId - The group root ID to navigate to
     */
    const handleVersionsClick = ({groupRootId}) => {
        navigate(`/learningCenter/archived/${groupRootId}`);
    };

    if (isLoading) {
        return <LoadingSpinner local={true}/>;
    }

    return (
        <div className="MyArchivedSignDocs">
            <div className="PageSecondaryTitle signDocsTitle">
                {i18n.translate('learningCenter.archivedSignDocs')}
            </div>
            <div className="archivedSection">
                {!filteredGroups || filteredGroups.length === 0 ? (
                    <div className="noArchivedSignDocs">
                        {isEmptyDueToFilters
                            ? i18n.translate('LearningManagementPage.noSearchResults')
                            : i18n.translate('learningCenter.noArchivedSignDocs')
                        }
                    </div>
                ) : (
                    <div className="ArchivedSignDocsWrapper">
                        {map(filteredGroups, (archivedGroup) => {
                            const signDoc = get(archivedGroup, 'signDoc', {});
                            const hasMultipleVersions = archivedGroup.totalVersions > 1;

                            return (
                                <div
                                    className="archivedGroupWrapper"
                                    key={archivedGroup.groupRootId}
                                    data-testid={`archived-group-${archivedGroup.groupRootId}`}
                                >
                                    <SignDocCard
                                        {...archivedGroup}
                                        {...signDoc}
                                        additionalData={get(archivedGroup, 'additionalData', {})}
                                        signDocAdditionalData={get(signDoc, 'additionalData', {})}
                                        status="archived"
                                        // For multiple versions: override button to show "גרסאות" and navigate to versions page
                                        // For single version: use default card behavior (opens the SignDoc)
                                        overrideButtonText={hasMultipleVersions ? i18n.translate('learningCenter.versions') : undefined}
                                        onOverrideButtonClick={hasMultipleVersions ? () => handleVersionsClick({groupRootId: archivedGroup.groupRootId}) : undefined}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyArchivedSignDocsContent;
